import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { Box, Button, Typography } from '@mui/material';
import { StakeErrorType } from '../../../../@types';
import { type BetslipEvent, useBetslipContext } from '../../../../contexts/BetslipContext';
import { checkIsWaysEventForBetslip } from '../../../../helpers';
import { OddsChangeStatus } from '../../../../helpers/betslipMessageParser';
import type { NeobetBetslipDrawerController } from '../../../../hooks/components/useNeobetBetslipDrawer';
import { WalletType } from '../../../../types/enums';
import { BetDetails } from '../../../atoms/BetDetails';
import OddsDisplay from '../../../atoms/OddsDisplay';
import LiveDot from '../atoms/LiveDot';
import NeobetRemoveRail from '../atoms/NeobetRemoveRail';
import NeobetBetslipStakeInput from './NeobetBetslipStakeInput';
import NeobetBetslipStakeMessage from './NeobetBetslipStakeMessage';

const getOddsBadgeColors = (oddsChange?: OddsChangeStatus) => {
  if (oddsChange === OddsChangeStatus.increased) {
    return {
      color: 'success.400',
    };
  }

  if (oddsChange === OddsChangeStatus.decreased) {
    return {
      color: 'error.300',
    };
  }

  return {
    color: 'primary.main',
  };
};

const getOddsChangeArrow = (oddsChange?: OddsChangeStatus) => {
  if (oddsChange === OddsChangeStatus.increased) {
    return <ArrowDropUpIcon data-testid='neobet-odds-change-arrow-increased' sx={styles.oddsChangeIcon} />;
  }

  if (oddsChange === OddsChangeStatus.decreased) {
    return <ArrowDropDownIcon data-testid='neobet-odds-change-arrow-decreased' sx={styles.oddsChangeIcon} />;
  }

  return null;
};

const styles = {
  root: (isWaysEvent: boolean, isDisabled: boolean) => ({
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '44px minmax(0, 1fr) auto',
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'neutral.600',
    overflow: 'hidden',
    opacity: isDisabled ? 0.5 : 1,
    ...(isWaysEvent
      ? {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: 4,
            backgroundColor: 'success.main',
            pointerEvents: 'none',
            zIndex: 1,
          },
        }
      : {}),
  }),
  betCardBody: {
    minWidth: 0,
    px: 1.25,
    py: 1,
    display: 'grid',
    alignContent: 'center',
    gap: 0.625,
  },
  betMeta: {
    minWidth: 0,
    display: 'grid',
    gap: 0.25,
  },
  betOutcome: {
    typography: 'body2',
    fontWeight: 800,
    color: 'text.primary',
    textAlign: 'left',
  },
  cardLiveDot: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: 1,
  },
  betEvent: {
    typography: 'subtitle3',
    color: 'neutral.200',
    lineHeight: 1.25,
  },
  betMarket: {
    typography: 'body3',
    color: 'neutral.200',
  },
  oddsBadge: (oddsChange?: OddsChangeStatus) => ({
    ...getOddsBadgeColors(oddsChange),
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.125,
    textAlign: 'left',
    alignSelf: 'flex-start',
  }),
  oddsChangeIcon: {
    fontSize: 18,
  },
  oddsValue: (oddsChange?: OddsChangeStatus) => ({
    ...getOddsBadgeColors(oddsChange),
    display: 'inline-block',
    typography: 'body1',
    fontWeight: 800,
  }),
  betCardSide: {
    px: 1.25,
    py: 1,
    display: 'grid',
    justifyItems: 'end',
    alignContent: 'end',
  },
  bankerButton: (active: boolean) => ({
    minWidth: 52,
    width: 52,
    height: 32,
    borderRadius: 2,
    backgroundColor: active ? 'primary.main' : 'neutral.500',
    color: active ? 'background.default' : 'text.primary',
    fontWeight: 800,
  }),
} as const;

type NeobetBetslipSelectionCardProps = {
  bet: BetslipEvent;
  selectedMenuItem: NeobetBetslipDrawerController['selectedMenuItem'];
  primaryStakeMode: NeobetBetslipDrawerController['primaryStakeMode'];
  handleStakeChange: NeobetBetslipDrawerController['handleStakeChange'];
  removeSingleStakeError: NeobetBetslipDrawerController['removeSingleStakeError'];
  updateSinglesStakeAmount: NeobetBetslipDrawerController['updateSinglesStakeAmount'];
  selectedWallet: WalletType;
  stakeErrorType?: StakeErrorType;
};

const NeobetBetslipSelectionCard = ({
  bet,
  selectedMenuItem,
  primaryStakeMode,
  handleStakeChange,
  removeSingleStakeError,
  updateSinglesStakeAmount,
  selectedWallet,
  stakeErrorType,
}: NeobetBetslipSelectionCardProps) => {
  const { bets, removeBet, toggleBanker, isMaxBankerCountReached } = useBetslipContext();
  const showBankerButton = selectedMenuItem === 'bankers';
  const isReadOnlySingle = primaryStakeMode === 'single';
  const isWaysEvent = checkIsWaysEventForBetslip(bet, bets);
  const isDisabled = Boolean(bet.disabled);

  return (
    <Box
      sx={styles.root(isWaysEvent, isDisabled)}
      data-testid={`neobet-selection-card-${bet.outcomeId}`}
      data-ways-event={isWaysEvent ? 'true' : 'false'}
    >
      <NeobetRemoveRail
        onClick={() => {
          removeBet(bet.outcomeId);
          removeSingleStakeError(bet.outcomeId);
        }}
      />
      {bet.isLive && <LiveDot sx={styles.cardLiveDot} />}
      <Box sx={styles.betCardBody}>
        <Box sx={styles.betMeta}>
          <BetDetails.Outcome
            outcomeName={bet.outcomeName ?? bet.outcomeId}
            outcomeShortName={bet.outcomeShortName}
            marketName={bet.marketName}
            marketTypeName={bet.marketType?.name}
            marketDisplayType={bet.marketDisplayType}
            eventName={bet.eventName}
            {...bet.properties}
            sx={styles.betOutcome}
          />
          <Typography sx={styles.betEvent}>{bet.eventName ?? bet.outcomeId}</Typography>
          <BetDetails.Market marketName={bet.marketName} marketShortName={bet.marketShortName} sx={styles.betMarket} />
        </Box>
        <Typography
          component='span'
          sx={styles.oddsBadge(bet.oddsChange)}
          data-testid={`neobet-odds-badge-${bet.outcomeId}`}
        >
          <OddsDisplay
            value={bet.odds}
            component='span'
            sx={styles.oddsValue(bet.oddsChange)}
            data-testid={`neobet-odds-value-${bet.outcomeId}`}
          />
          {getOddsChangeArrow(bet.oddsChange)}
        </Typography>
      </Box>
      <Box sx={styles.betCardSide}>
        {showBankerButton ? (
          <Button
            sx={styles.bankerButton(Boolean(bet.banker))}
            disabled={isMaxBankerCountReached && !bet.banker}
            onClick={() => toggleBanker(bet.outcomeId)}
          >
            B
          </Button>
        ) : isReadOnlySingle ? null : (
          <NeobetBetslipStakeInput
            value={bet.singlesStakeAmount?.toString() ?? ''}
            width={88}
            disabled={isDisabled}
            helperMessage={
              stakeErrorType ? (
                <NeobetBetslipStakeMessage
                  errorType={stakeErrorType}
                  ticketType={bet.isLive ? 'inPlay' : 'preMatch'}
                  selectedWallet={selectedWallet}
                  testId={`neobet-selection-card-stake-message-${bet.outcomeId}`}
                />
              ) : undefined
            }
            onChange={(value) =>
              handleStakeChange(
                bet.outcomeId,
                value,
                updateSinglesStakeAmount,
                true,
                bet.isLive ? 'inPlay' : 'preMatch',
                1
              )
            }
            testId={`neobet-selection-card-stake-${bet.outcomeId}`}
          />
        )}
      </Box>
    </Box>
  );
};

export default NeobetBetslipSelectionCard;
